import React, { useEffect, useState } from "react";
import { X, Loader2, Share2, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const NICE = {
  facebook: "Facebook", instagram: "Instagram", threads: "Threads",
  x: "X (Twitter)", tiktok: "TikTok", youtube: "YouTube",
};

export default function MontageSocialModal({ montage, stationName, stationId, onClose }) {
  const [conns, setConns] = useState(null);
  const [sel, setSel] = useState({});
  const requestLink = stationId
    ? `${window.location.origin}/requests/${stationId}?utm_source=shoutout_roundup&utm_medium=social`
    : "";
  const [caption, setCaption] = useState(
    `This week's listener shoutouts on ${stationName || "our station"} — real voices from real listeners. Want to hear yours on air? Record your shoutout here: ${requestLink}`.trim()
  );
  const [posting, setPosting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/social/status").then((r) => {
      const conn = (r.data.platforms || []).filter((p) => p.connected && NICE[p.platform]);
      setConns(conn);
      setSel(Object.fromEntries(conn.map((c) => [c.platform, ["facebook", "instagram"].includes(c.platform)])));
    }).catch(() => setConns([]));
  }, []);

  const chosen = Object.keys(sel).filter((k) => sel[k]);

  const publish = async () => {
    if (!chosen.length) { toast.error("Pick at least one platform"); return; }
    setPosting(true);
    try {
      const { data } = await api.post(`/request-line/montage/${montage.montage_id}/social-post`,
        { platforms: chosen, caption });
      setResults(data.results);
      const ok = data.results.filter((r) => r.ok).length;
      if (ok) toast.success(`Posted to ${ok} platform${ok === 1 ? "" : "s"}`);
      else toast.error("Publishing failed — see details below");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setPosting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="montage-social-modal">
      <div className="w-full max-w-md max-h-[88vh] overflow-y-auto rounded-xl border border-line bg-bg-surface p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="inline-flex items-center gap-2 font-display font-bold text-lg">
            <Share2 className="h-4.5 w-4.5 text-brand-amber" /> Post to socials
          </div>
          <button onClick={onClose} data-testid="montage-social-close"
            className="w-7 h-7 rounded-md flex items-center justify-center border border-line text-ink-muted hover:text-ink-primary transition">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="text-xs text-ink-secondary mb-4">
          "{montage.title}" becomes a branded waveform video your followers can hear right in the feed.
        </div>

        {results ? (
          <div className="space-y-2" data-testid="montage-social-results">
            {results.map((r) => (
              <div key={r.platform} data-testid={`social-result-${r.platform}`}
                className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[12px] ${
                  r.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"
                }`}>
                {r.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                      : <AlertCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <div className="font-semibold">{NICE[r.platform] || r.platform}</div>
                  {r.ok ? (
                    r.external_url ? (
                      <a href={r.external_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-emerald-300 hover:underline">
                        View post <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : <span className="text-emerald-300">Published</span>
                  ) : (
                    <span className="text-red-300 break-words">{r.error}</span>
                  )}
                </div>
              </div>
            ))}
            <button onClick={onClose} data-testid="montage-social-done"
              className="w-full mt-2 rounded-md border border-line bg-bg-elevated py-2.5 text-sm font-semibold text-ink-secondary hover:text-brand-amber transition">
              Done
            </button>
          </div>
        ) : (
          <>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">Platforms</label>
            {conns === null ? (
              <div className="text-xs text-ink-muted py-4"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" />Checking connections...</div>
            ) : conns.length === 0 ? (
              <div className="text-[12px] text-ink-muted border border-dashed border-line rounded-lg p-4 mb-4" data-testid="montage-social-noconn">
                No social accounts connected yet — connect Facebook or Instagram under Settings → Social Connections, then come back.
              </div>
            ) : (
              <div className="flex flex-col gap-1.5 mb-4">
                {conns.map((c) => (
                  <label key={c.platform} data-testid={`social-pick-${c.platform}`}
                    className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition text-[12.5px] ${
                      sel[c.platform] ? "border-brand-amber/60 bg-brand-amber/5" : "border-line bg-bg-elevated"
                    }`}>
                    <input type="checkbox" checked={!!sel[c.platform]}
                      onChange={(e) => setSel((s) => ({ ...s, [c.platform]: e.target.checked }))}
                      className="accent-[#f5a623]" />
                    <span className="font-semibold">{NICE[c.platform]}</span>
                    <span className="text-[10.5px] text-ink-muted truncate">{c.account_label}</span>
                  </label>
                ))}
              </div>
            )}

            <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">Caption</label>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={4} maxLength={2000}
              data-testid="montage-social-caption"
              className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs outline-none focus:border-brand-amber/60 resize-none mb-4" />

            <button onClick={publish} disabled={posting || !chosen.length} data-testid="montage-social-publish"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-brand-amber py-3 text-sm font-bold text-black amber-glow disabled:opacity-50">
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
              {posting ? "Building video & posting…" : `Post now (${chosen.length})`}
            </button>
            <div className="mt-2 text-[10.5px] text-ink-muted text-center">
              First post builds the video — can take ~20 seconds.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
